import type { Invoice } from "@be-miniapp/api"

interface StoredPartnerInvoice {
  userId: number
  partnerIdentifier: string
  invoice: Invoice
  createdAt: string
}

const STORAGE_KEY = "be_partner_invoices"

/**
 * Gets the local storage key for partner invoices
 */
const getStorageData = (): StoredPartnerInvoice[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.warn("Failed to parse partner invoice storage:", error)
    return []
  }
}

/**
 * Saves invoice data to local storage with user and partner context
 */
export const storePartnerInvoice = (
  userId: number,
  partnerIdentifier: string,
  invoice: Invoice
): void => {
  try {
    const existingData = getStorageData()

    // Remove any existing invoice for this user-partner combination
    const filteredData = existingData.filter(
      (item) =>
        !(
          item.userId === userId && item.partnerIdentifier === partnerIdentifier
        )
    )

    // Add new invoice data
    const newEntry: StoredPartnerInvoice = {
      userId,
      partnerIdentifier,
      invoice,
      createdAt: new Date().toISOString(),
    }

    filteredData.push(newEntry)

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredData))
  } catch (error) {
    console.error("Failed to store partner invoice:", error)
  }
}

/**
 * Retrieves stored invoice for specific user and partner
 */
export const getStoredPartnerInvoice = (
  userId: number,
  partnerIdentifier: string
): Invoice | null => {
  try {
    const storedData = getStorageData()

    const found = storedData.find(
      (item) =>
        item.userId === userId && item.partnerIdentifier === partnerIdentifier
    )

    return found ? found.invoice : null
  } catch (error) {
    console.error("Failed to retrieve partner invoice:", error)
    return null
  }
}

/**
 * Clears stored invoice for specific user and partner
 */
export const clearStoredPartnerInvoice = (
  userId: number,
  partnerIdentifier: string
): void => {
  try {
    const existingData = getStorageData()

    const filteredData = existingData.filter(
      (item) =>
        !(
          item.userId === userId && item.partnerIdentifier === partnerIdentifier
        )
    )

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredData))
  } catch (error) {
    console.error("Failed to clear partner invoice:", error)
  }
}

/**
 * Clears all stored invoices for a specific user (useful for logout)
 */
export const clearUserPartnerInvoices = (userId: number): void => {
  try {
    const existingData = getStorageData()

    const filteredData = existingData.filter((item) => item.userId !== userId)

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredData))
  } catch (error) {
    console.error("Failed to clear user partner invoices:", error)
  }
}

/**
 * Gets all stored invoices for debugging purposes
 */
export const getAllStoredPartnerInvoices = (): StoredPartnerInvoice[] => {
  return getStorageData()
}
